import { useRef } from "react";
import { useIbkrData, useVisible } from "./poller";
import { ClosedTrades } from "./types";
import { C, IntroHeader, SideTag, SymbolCell, WidgetBox, fmt, freshnessDot, useNarrow } from "./ui";
import { WidgetProps } from "./windows";

const GRID = "1.9fr .7fr .9fr 1fr 1.3fr";

function Pnl({ value }: { value: number | null }) {
  if (value == null) return <span style={{ textAlign: "right", fontSize: 13 }}>—</span>;
  const up = value >= 0;
  return (
    <span style={{ textAlign: "right", fontSize: 13, fontWeight: 600, color: up ? C.gain : C.loss, whiteSpace: "nowrap" }}>
      {up ? "▲ +" : "▼ −"}{Math.abs(value).toFixed(2)}
    </span>
  );
}

const time = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

// today's closed trades (orders that closed a position) with IB's realized
// P&L per trade, net of commissions; IB's API only provides the current day
export function TradesWidget({ widget, actions, intro }: WidgetProps) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useVisible(ref);
  const narrow = useNarrow(ref);
  const { data, freshness, error } = useIbkrData<ClosedTrades>("trades", {}, visible);
  const trades = data?.trades ?? [];

  return (
    <>
      <IntroHeader stampLine={intro} text={`${trades.length} closed trades today, realized P&L ${data ? data.realizedTotal.toFixed(2) : "…"}.`} />
      <WidgetBox boxRef={ref} headerAlign="center" title={<span style={{ fontWeight: 600 }}>Closed trades today ({trades.length})</span>}
        status={freshnessDot(freshness)} onDuplicate={() => actions.duplicate({ ...widget.params })} onClose={actions.close}>
        {!narrow && <div style={{ display: "grid", gridTemplateColumns: GRID, gap: 8, alignItems: "center", padding: "6px 10px", color: C.muted, borderBottom: `1px solid ${C.divider}` }}>
          <span>Symbol</span><span style={{ textAlign: "right" }}>Qty</span><span style={{ textAlign: "right" }}>Price</span>
          <span style={{ textAlign: "right" }}>Time</span><span style={{ textAlign: "right" }}>P&amp;L</span>
        </div>}
        {trades.length === 0 && <div style={{ padding: "8px 10px", color: C.muted }}>{error ?? (data ? "No closed trades today." : "Loading…")}</div>}
        {trades.map((t, i) => {
          const key = t.permId || `${t.orderId}-${i}`;
          const border = i > 0 ? `1px solid ${C.divider}` : undefined;
          // phones: symbol over "qty @ price · time", P&L on the right
          if (narrow) {
            return (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderTop: border }}>
                <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                  <SymbolCell tag={<SideTag side={t.side} />} contract={t.contract} inline />
                  <span style={{ color: C.muted }}>{t.size} @ {fmt(t.avgPrice)} · {time(t.time)}</span>
                </span>
                <Pnl value={t.realizedPnl} />
              </div>
            );
          }
          return (
            <div key={key} style={{ display: "grid", gridTemplateColumns: GRID, gap: 8, alignItems: "center", padding: "8px 10px", borderTop: border }}>
              <SymbolCell tag={<SideTag side={t.side} />} contract={t.contract} />
              <span style={{ textAlign: "right", fontSize: 13 }}>{t.size}</span>
              <span style={{ textAlign: "right", fontSize: 13 }}>{fmt(t.avgPrice)}</span>
              <span style={{ textAlign: "right", color: C.muted }}>{time(t.time)}</span>
              <Pnl value={t.realizedPnl} />
            </div>
          );
        })}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", borderTop: `1px solid ${C.border}`, background: C.headerBg }}>
          <span style={{ color: C.muted }}>Realized total{data ? ` (commissions ${data.commissionTotal.toFixed(2)})` : ""}:</span>
          <Pnl value={data ? data.realizedTotal : null} />
        </div>
      </WidgetBox>
    </>
  );
}
